import { create } from 'zustand';
import type { StockLocationDto, StockLocationInput } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';

/**
 * Warehouses and branches (ADR-089) — searched and paged on the server; added,
 * changed and deleted here. What each holds is the inventory screen's.
 */

interface StockLocationsState {
  items: StockLocationDto[];
  nextCursor: string | null;
  total: number | null;
  search: string;
  loading: boolean;
  error: string | null;
  saving: boolean;
  saveError: string | null;

  load: (filters?: { search?: string }) => Promise<void>;
  loadMore: () => Promise<void>;
  save: (id: string | null, input: StockLocationInput) => Promise<boolean>;
  remove: (id: string) => Promise<boolean>;
  clearSaveError: () => void;
}

const PAGE_SIZE = 20;

export const useStockLocationsStore = create<StockLocationsState>((set, get) => ({
  items: [],
  nextCursor: null,
  total: null,
  search: '',
  loading: false,
  error: null,
  saving: false,
  saveError: null,

  async load(filters = {}) {
    const search = filters.search ?? get().search;
    set({ loading: true, error: null, search });
    try {
      const page = await withSession(() => adminApi.stockLocations.list({ limit: PAGE_SIZE, ...(search.trim() ? { search: search.trim() } : {}) }));
      if (get().search !== search) return;
      set({ items: page.items, nextCursor: page.meta.nextCursor, total: page.meta.total ?? null, loading: false });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load the locations.') });
    }
  },

  async loadMore() {
    const { nextCursor, search, loading } = get();
    if (!nextCursor || loading) return;
    set({ loading: true });
    try {
      const page = await withSession(() =>
        adminApi.stockLocations.list({ limit: PAGE_SIZE, cursor: nextCursor, ...(search.trim() ? { search: search.trim() } : {}) }),
      );
      set((state) => ({ items: [...state.items, ...page.items], nextCursor: page.meta.nextCursor, loading: false }));
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load more locations.') });
    }
  },

  async save(id, input) {
    set({ saving: true, saveError: null });
    try {
      const saved = await withSession(() => (id ? adminApi.stockLocations.update(id, input) : adminApi.stockLocations.create(input)));
      set((state) => ({
        saving: false,
        items: id ? state.items.map((item) => (item.id === id ? saved : item)) : [...state.items, saved],
        total: id || state.total === null ? state.total : state.total + 1,
      }));
      return true;
    } catch (error) {
      set({ saving: false, saveError: errorMessage(error, 'Could not save the location.') });
      return false;
    }
  },

  async remove(id) {
    set({ saving: true, saveError: null });
    try {
      await withSession(() => adminApi.stockLocations.remove(id));
      set((state) => ({ saving: false, items: state.items.filter((item) => item.id !== id), total: state.total === null ? null : state.total - 1 }));
      return true;
    } catch (error) {
      set({ saving: false, saveError: errorMessage(error, 'Could not delete the location.') });
      return false;
    }
  },

  clearSaveError() {
    set({ saveError: null });
  },
}));
